import { useMemo, useState } from 'react';
import { useBlockedRefunds, useBlockedRefundsDates } from '../hooks/useBlockedRefunds';

const number = (n: number) => n.toLocaleString('en-US');

function formatDate(date: string): string {
  return new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Timestamps arrive as `YYYY-MM-DD HH:MM:SS PDT|PST`; JS can't parse the zone
// abbreviation, so day-level age is computed from the date part alone.
function daysSince(pacificTs: string, refMs: number): number | null {
  const day = pacificTs.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  return Math.max(0, Math.floor((refMs - new Date(day + 'T00:00:00').getTime()) / DAY_MS));
}

interface StatusMeta {
  badgeCls: string;
  explanation: string;
  needsRefund: boolean;
}

const STATUS_META: Record<string, StatusMeta> = {
  paid: {
    badgeCls: 'bg-red-100 text-red-800',
    explanation: 'Not yet refunded — needs a manual refund in Shopify',
    needsRefund: true,
  },
  partially_paid: {
    badgeCls: 'bg-amber-100 text-amber-800',
    explanation: 'Partially paid — review and refund manually in Shopify',
    needsRefund: true,
  },
  partially_refunded: {
    badgeCls: 'bg-amber-100 text-amber-800',
    explanation: 'Partially refunded — remaining amount may need a manual refund',
    needsRefund: true,
  },
  refunded: {
    badgeCls: 'bg-green-100 text-green-800',
    explanation: 'Already fully refunded in Shopify — no action needed',
    needsRefund: false,
  },
  voided: {
    badgeCls: 'bg-gray-100 text-gray-600',
    explanation: 'Payment voided — no refund needed',
    needsRefund: false,
  },
  unknown: {
    badgeCls: 'bg-gray-100 text-gray-600',
    explanation: 'Order not found in Shopify data — investigate',
    needsRefund: true,
  },
};

const UNRECOGNIZED_STATUS_META: StatusMeta = {
  badgeCls: 'bg-gray-100 text-gray-600',
  explanation: 'Unrecognized financial status',
  needsRefund: true,
};

const statusMeta = (status: string): StatusMeta => STATUS_META[status] ?? UNRECOGNIZED_STATUS_META;

function StatusBadge({ status }: { status: string }) {
  const meta = statusMeta(status);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${meta.badgeCls}`}
      title={meta.explanation}
    >
      {status}
    </span>
  );
}

const INITIAL_ROWS = 50;

export function BlockedRefunds() {
  const { data: dates, isLoading: datesLoading } = useBlockedRefundsDates();
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const effectiveDate = selectedDate ?? dates?.[0]?.date;

  const { data: report, isLoading, error } = useBlockedRefunds(effectiveDate);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showAllRows, setShowAllRows] = useState(false);

  // Age is measured against the snapshot's generation time so historical days stay accurate.
  const refMs = useMemo(
    () => (report?.generatedAt ? new Date(report.generatedAt).getTime() : Date.now()),
    [report?.generatedAt]
  );

  const statusCounts = report?.summary.byFinancialStatus ?? {};
  const cardStatuses = Object.keys(statusCounts).sort(
    (a, b) => Number(statusMeta(b).needsRefund) - Number(statusMeta(a).needsRefund)
  );
  const needsRefundCount = cardStatuses.reduce(
    (sum, s) => (statusMeta(s).needsRefund ? sum + (statusCounts[s] ?? 0) : sum),
    0
  );

  const visibleRows = useMemo(() => {
    let rows = report?.rows ?? [];
    if (statusFilter) {
      rows = rows.filter((r) => (r.shopifyFinancialStatus ?? 'unknown') === statusFilter);
    }
    const s = search.trim().toLowerCase();
    if (s) {
      rows = rows.filter((r) => r.shopifyOrderId.toLowerCase().includes(s));
    }
    // Oldest orders first — they've been blocked the longest
    return [...rows].sort((a, b) => a.shopifyCreatedAt.localeCompare(b.shopifyCreatedAt));
  }, [report, statusFilter, search]);

  const displayedRows = showAllRows ? visibleRows : visibleRows.slice(0, INITIAL_ROWS);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Blocked Refunds</h1>
          <p className="mt-1 text-sm text-gray-500">
            Daily snapshot of orders stuck in Delivery Pending with rejected items — CHQ payment
            issues block AirBoss from refunding them, so each needs a manual refund in Shopify.
            Read-only — informational for now.
            {report?.generatedAt && (
              <span className="text-gray-400">
                {' '}Generated {new Date(report.generatedAt).toLocaleString('en-US')}.
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="blocked-refunds-date" className="text-sm text-gray-500">
            Date
          </label>
          <select
            id="blocked-refunds-date"
            value={effectiveDate ?? ''}
            onChange={(e) => setSelectedDate(e.target.value)}
            disabled={datesLoading || !dates || dates.length === 0}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
          >
            {dates?.map((d) => (
              <option key={d.date} value={d.date}>
                {formatDate(d.date)} ({d.rowCount})
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-red-600">
            Error loading blocked refunds: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      )}

      {!error && !datesLoading && dates && dates.length === 0 && (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900">No reports available</h3>
          <p className="mt-1 text-sm text-gray-500">
            No blocked-refunds reports were found for the last 30 days.
          </p>
        </div>
      )}

      {(isLoading || datesLoading) && (
        <div className="space-y-4 animate-pulse">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <div className="h-4 bg-gray-200 rounded w-24" />
                <div className="mt-3 h-7 bg-gray-200 rounded w-16" />
              </div>
            ))}
          </div>
          <div className="h-64 bg-white rounded-lg border border-gray-200" />
        </div>
      )}

      {!isLoading && !error && report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              type="button"
              onClick={() => setStatusFilter(null)}
              className={`bg-white rounded-lg border shadow-sm p-4 text-left ${
                statusFilter === null ? 'border-blue-400 ring-1 ring-blue-400' : 'border-gray-200'
              }`}
            >
              <p className="text-sm text-gray-500">Blocked orders</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {number(report.summary.orderCount)}
              </p>
              <p className="mt-1 text-xs text-gray-400 truncate">
                {number(needsRefundCount)} likely need a manual refund
              </p>
            </button>
            {cardStatuses.map((status) => {
              const meta = statusMeta(status);
              const active = statusFilter === status;
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => setStatusFilter(active ? null : status)}
                  className={`bg-white rounded-lg border shadow-sm p-4 text-left ${
                    active ? 'border-blue-400 ring-1 ring-blue-400' : 'border-gray-200'
                  }`}
                  title={meta.explanation}
                >
                  <p className="text-sm text-gray-500 truncate">{status}</p>
                  <p
                    className={`mt-1 text-2xl font-semibold ${
                      meta.needsRefund ? 'text-amber-600' : 'text-gray-900'
                    }`}
                  >
                    {number(statusCounts[status] ?? 0)}
                  </p>
                  <p className="mt-1 text-xs text-gray-400 truncate">{meta.explanation}</p>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search order id…"
              className="w-full sm:w-96 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {statusFilter && (
              <button
                type="button"
                onClick={() => setStatusFilter(null)}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Clear filter: {statusFilter} ×
              </button>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Financial Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fulfillment Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Age</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayedRows.map((row) => {
                    const ageDays = daysSince(row.shopifyCreatedAt, refMs);
                    return (
                      <tr key={row.shopifyOrderId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <a
                            href={`https://admin.shopify.com/store/skimsbody/orders/${row.shopifyOrderId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-blue-600 hover:text-blue-700"
                          >
                            {row.shopifyOrderId}
                          </a>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <StatusBadge status={row.shopifyFinancialStatus ?? 'unknown'} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {row.shopifyFulfillmentStatus ?? '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {row.shopifyCreatedAt.slice(0, 10)}
                        </td>
                        <td
                          className="px-4 py-3 whitespace-nowrap text-sm text-gray-700"
                          title={`Created: ${row.shopifyCreatedAt}`}
                        >
                          {ageDays !== null ? `${number(ageDays)}d` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {visibleRows.length === 0 && (
              <div className="text-center py-12">
                <p className="text-sm text-gray-500">No orders match the current filters.</p>
              </div>
            )}

            {visibleRows.length > 0 && (
              <div className="text-center py-3 border-t border-gray-100">
                <span className="text-sm text-gray-500">
                  Showing {number(displayedRows.length)} of {number(visibleRows.length)} rows
                </span>
                {visibleRows.length > INITIAL_ROWS && (
                  <button
                    type="button"
                    onClick={() => setShowAllRows((v) => !v)}
                    className="ml-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    {showAllRows ? 'Show less' : 'Show all'}
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
